import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { QuestionnaireRequest } from 'src/API';
import {
  ConsentFormResponse,
  getLatestConsentFormResponses
} from 'src/app/core/api/consent-form.util';
import { Patient } from 'src/app/core/api/patient.service';
import { AppStateService } from './app-state.service';

interface PatientConsentStatus {
  patientId: string | null;
  latestResponses: ConsentFormResponse[];
}

function getPatientQuestionnaires(patient: Patient): QuestionnaireRequest[] {
  return patient && patient.questionnaireRequests && patient.questionnaireRequests.items
    ? (patient.questionnaireRequests.items as QuestionnaireRequest[])
    : [];
}

function isSameConsentStatus(previous: PatientConsentStatus, next: PatientConsentStatus): boolean {
  return (
    previous.patientId === next.patientId &&
    previous.latestResponses.length === next.latestResponses.length &&
    previous.latestResponses.every(
      (response, index) =>
        response.questionnaire.id === next.latestResponses[index].questionnaire.id &&
        response.status === next.latestResponses[index].status
    )
  );
}

/**
 * Where a patient stands on each consent form, derived from AppStateService rather than held as a
 * second copy, so patient switches and questionnaire updates flow through automatically.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientConsentStatusService {
  // AppStateService re-emits the same patient object after mutating its questionnaire list in
  // place, so this recomputes on every emission and de-duplicates on the derived result instead.
  private readonly currentPatientConsentStatus$: Observable<
    PatientConsentStatus
  > = this.appStateService.currentPatient$.pipe(
    map(patient => ({
      patientId: patient ? patient.id : null,
      latestResponses: getLatestConsentFormResponses(getPatientQuestionnaires(patient))
    })),
    distinctUntilChanged(isSameConsentStatus),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(private appStateService: AppStateService) {}

  /**
   * Latest completed response to each consent form, newest first. Empty unless `patientId` is the
   * current patient, since screens such as referrals can render while app state still holds the
   * previously viewed patient.
   */
  public getLatestConsentFormResponsesForPatient(
    patientId: string
  ): Observable<ConsentFormResponse[]> {
    return this.currentPatientConsentStatus$.pipe(
      map(status => (!!patientId && status.patientId === patientId ? status.latestResponses : [])),
      distinctUntilChanged()
    );
  }

  public getDeclinedConsentFormResponsesForPatient(
    patientId: string
  ): Observable<ConsentFormResponse[]> {
    return this.getLatestConsentFormResponsesForPatient(patientId).pipe(
      map(responses => responses.filter(response => response.status === 'rejected'))
    );
  }
}
